/**
 * Gift With Purchase (GWP) Manager
 *
 * Keeps the cart in ONE target state, re-checked after every cart change:
 *   - promo conditions met  → exactly one gift unit in the cart
 *   - conditions not met    → no gift in the cart
 *
 * Conditions: market eligible, today (America/Toronto) within the inclusive
 * start/end dates, and spend on qualifying items ≥ threshold. Spend = line
 * prices AFTER all cart discounts (product- and order-level codes/automatic
 * discounts), before tax, of items in the configured collection — or of every
 * item when no collection is set. Gift cards are a payment, not a discount, so
 * they don't reduce it. The gift line never counts.
 *
 * How it stays consistent:
 *   - Every run fetches a FRESH cart (section `gwp-cart-data`) and computes the
 *     fix from it — never from a caller's snapshot.
 *   - Runs are serialized: one at a time, later requests coalesce into a run
 *     that already sees the latest cart. Two runs can never both decide "no gift
 *     yet" and add it twice (the old duplicate-gift bug).
 *   - Any successful POST to /cart/add|change|update|clear made through fetch()
 *     — by the theme or by an app — schedules a run, so removals/decreases are
 *     covered even on paths that never call processGWP.
 *   - Clamps quantity > 1 and merges duplicate gift lines back to one unit.
 *   - After changing the cart it dispatches `cart:refresh` so the drawer / cart
 *     page re-render (cart-drawer.js).
 *
 * Config comes from `[data-gwp-config]` (snippets/cart-gwp-js-variables.liquid).
 * Checkout-side enforcement (express checkout, direct links) is NOT possible
 * from the theme — that is the job of the checkout validation function.
 *
 * Public API (unchanged): window.processGWP(cartData, source) → Promise<result>,
 * the `gwp:process` event, window.gwpManager. Debug logs: add ?gwp_debug=1.
 */

const GWP_DEBUG = (() => {
  try {
    return /[?&]gwp_debug=1/.test(window.location.search);
  } catch (e) {
    return false;
  }
})();

function gwpLog(...args) {
  if (GWP_DEBUG) console.log('[GWP]', ...args);
}

/* Line item properties as a plain { key: value } object. The cart JSON rendered
   by Liquid (`item.properties | json`) gives an array of [key, value] pairs,
   while /cart.js gives an object, so accept both. Reading the pairs array as an
   object made every gift look like it had the wrong note, and each cart change
   rewrote the gift line three times (2026-11-04, slow add/remove). */
function gwpPropsObject(props) {
  const out = {};
  if (!props) return out;
  const entries = Array.isArray(props) ? props : Object.entries(props);
  for (const entry of entries) {
    if (!Array.isArray(entry) || entry.length < 2) continue;
    const [k, v] = entry;
    if (k == null || v === null || v === undefined || v === '') continue;
    out[String(k)] = String(v);
  }
  return out;
}

const GWP_CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const GWP_CHECKOUT_LINK = /\/checkout\/?$/;
const GWP_CHECKOUT_MAX_WAIT_MS = 3000;
const GWP_DATE = /^\d{4}-\d{2}-\d{2}$/;

class GWPManager {
  constructor() {
    this.config = this.loadConfig();
    this.running = null; // { startedAt, promise }
    this.pending = null; // promise of the next queued run
    this.lastMutationAt = 0; // performance.now() of the last cart change seen

    // Our own requests use the unwrapped fetch so they don't re-trigger runs.
    this.fetch = window.fetch.bind(window);

    document.addEventListener('gwp:process', (e) => this.handleProcessEvent(e));

    if (!this.isActive()) {
      gwpLog('inactive', this.config);
      return;
    }

    this.observeCartMutations();
    this.guardCheckout();

    // A tab restored from bfcache or brought back to the front may hold a cart
    // changed elsewhere (another tab) — re-check it.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.request('pageshow');
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.request('visible');
    });

    this.processInitialCart();
  }

  /* ---------------------------------------------------------------- config */

  loadConfig() {
    const el = document.querySelector('[data-gwp-config]');
    if (!el) return null;

    try {
      const raw = JSON.parse(el.textContent);
      if (!raw.variantId || !raw.threshold) return null;

      return {
        enabled: raw.enabled !== false,
        variantId: Number(raw.variantId),
        threshold: Number(raw.threshold),
        startDate: GWP_DATE.test(raw.startDate || '') ? raw.startDate : null,
        endDate: GWP_DATE.test(raw.endDate || '') ? raw.endDate : null,
        collectionId: raw.collectionId || null,
        eligibleMarkets: Array.isArray(raw.eligibleMarkets) ? raw.eligibleMarkets.filter(Boolean) : [],
        currentMarket: raw.currentMarket || null,
        noteLabel: typeof raw.noteLabel === 'string' ? raw.noteLabel.trim() : '',
        noteText: typeof raw.noteText === 'string' ? raw.noteText.trim() : '',
        dismissKey: raw.localStorageKey || 'gwp-dismissed',
      };
    } catch (e) {
      console.warn('GWP: invalid config', e);
      return null;
    }
  }

  isActive() {
    return !!(this.config && this.config.enabled);
  }

  /* ------------------------------------------------------------ conditions */

  isGift(item) {
    return Number(item.variant_id || item.id) === this.config.variantId;
  }

  /* Properties the gift line must carry: the hidden `_gwp` flag plus, when the
     gift product has a note, a visible "Note" property in the shopper's
     language so it shows on checkout and the invoice. */
  giftProperties() {
    const props = { _gwp: 'true' };
    const { noteLabel, noteText } = this.config;
    if (noteLabel && noteText) props[noteLabel] = noteText;
    return props;
  }

  hasGiftProperties(item) {
    const want = this.giftProperties();
    const have = gwpPropsObject(item.properties);
    const keys = Object.keys(want);
    return keys.length === Object.keys(have).length && keys.every((k) => have[k] === want[k]);
  }

  isMarketEligible() {
    const { eligibleMarkets, currentMarket } = this.config;
    if (!eligibleMarkets.length) return true;
    if (!currentMarket) return true;
    return eligibleMarkets.includes(currentMarket);
  }

  /* Promo dates are store wall-clock days in Toronto, both ends inclusive:
     compare today's Toronto date as YYYY-MM-DD strings (no Date parsing — the
     old `new Date('YYYY-MM-DD')` was UTC midnight, which ended the promo on the
     evening BEFORE the end date). */
  isWithinDateRange() {
    const { startDate, endDate } = this.config;
    if (!startDate && !endDate) return true;

    let today;
    try {
      today = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Toronto',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date());
    } catch (e) {
      return true;
    }

    if (startDate && today < startDate) return false;
    if (endDate && today > endDate) return false;
    return true;
  }

  qualifyingSpend(cart) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const collectionId = this.config.collectionId;

    return items.reduce((sum, item) => {
      if (this.isGift(item)) return sum;
      if (collectionId && !(item.collections || []).includes(collectionId)) return sum;
      const amount = item.discounted_line_price ?? item.final_line_price ?? item.line_price;
      return sum + (Number(amount) || 0);
    }, 0);
  }

  eligibility(cart) {
    if (!this.isMarketEligible()) return { eligible: false, reason: 'market' };
    if (!this.isWithinDateRange()) return { eligible: false, reason: 'dates' };

    const spend = this.qualifyingSpend(cart);
    if (spend < this.config.threshold * 100) return { eligible: false, reason: 'below threshold', spend };

    return { eligible: true, reason: 'eligible', spend };
  }

  isDismissed() {
    try {
      return sessionStorage.getItem(this.config.dismissKey) === 'true';
    } catch (e) {
      return false;
    }
  }

  setDismissed(isDismissed = true) {
    try {
      sessionStorage.setItem(this.config.dismissKey, isDismissed ? 'true' : 'false');
    } catch (e) {
      /* storage disabled */
    }
  }

  /* What has to change so the cart matches the target state. Returns
     { reason } when nothing has to change. */
  plan(cart) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const gifts = items.filter((item) => this.isGift(item));
    const giftQty = gifts.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    const { eligible, reason, spend } = this.eligibility(cart);

    if (!eligible) {
      if (!gifts.length) return { reason, spend };
      return { reason, spend, remove: true, updates: this.updatesFor(gifts, null) };
    }

    if (!gifts.length) {
      if (this.isDismissed()) return { reason: 'dismissed', spend };
      return { reason, spend, add: true };
    }

    if (giftQty === 1) {
      // Wrong or missing note (older cart, or the shopper switched language) →
      // rewrite the gift line's properties.
      if (!this.hasGiftProperties(gifts[0])) return { reason: 'gift note', spend, relabel: gifts[0].key };
      return { reason: 'gift ok', spend };
    }

    // Quantity > 1 or several gift lines → keep one unit on one line.
    const keep = gifts.find((item) => gwpPropsObject(item.properties)._gwp) || gifts[0];
    return { reason: 'clamp to one', spend, updates: this.updatesFor(gifts, keep) };
  }

  updatesFor(gifts, keep) {
    const updates = {};
    for (const item of gifts) updates[item.key] = item === keep ? 1 : 0;
    return updates;
  }

  /* ------------------------------------------------------------ scheduling */

  /* Every trigger funnels through here. Coalesces into the in-flight run when
     that run started after the last cart change (it already sees the latest
     cart); otherwise queues exactly one follow-up run. */
  request(source) {
    if (!this.isActive()) return Promise.resolve(this.result({ reason: 'GWP not configured or disabled' }));

    const current = this.running;
    if (current && current.startedAt >= this.lastMutationAt) return current.promise;
    if (this.pending) return this.pending;

    const previous = current ? current.promise.catch(() => {}) : Promise.resolve();
    this.pending = previous.then(() => {
      this.pending = null;
      return this.start(source);
    });
    return this.pending;
  }

  start(source) {
    const run = { startedAt: performance.now() };
    run.promise = this.reconcile(source).finally(() => {
      if (this.running === run) this.running = null;
    });
    this.running = run;
    return run.promise;
  }

  /* Watch fetch() for cart changes made by anything else on the page. */
  observeCartMutations() {
    const manager = this;
    const previousFetch = window.fetch;

    window.fetch = function gwpObservedFetch(input, init) {
      const response = previousFetch.apply(this, arguments);
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        if (method === 'POST' && GWP_CART_MUTATION.test(url)) {
          response
            .then((res) => {
              if (!res.ok) return;
              manager.lastMutationAt = performance.now();
              manager.request('cart-mutation');
            })
            .catch(() => {});
        }
      } catch (e) {
        /* never break the caller's request */
      }
      return response;
    };
  }

  /* "Proceed to checkout" waits for one fresh gift check, so a cart that was
     just changed (gift link + fast click, another tab) reaches checkout fixed.

     FAIL-OPEN — the link can never stay blocked:
     - it stays a plain <a href>: no JS / this file failing → normal link;
     - a watchdog timer, armed BEFORE anything async, navigates after
       GWP_CHECKOUT_MAX_WAIT_MS whatever happens;
     - any error navigates immediately;
     - a second click while waiting is left to the browser (goes straight through);
     - ctrl/cmd/shift/middle clicks and target=_blank are untouched. */
  guardCheckout() {
    document.addEventListener(
      'click',
      (event) => {
        let link;
        try {
          link = event.target instanceof Element ? event.target.closest('a[href]') : null;
          if (!link) return;
          if (!GWP_CHECKOUT_LINK.test(new URL(link.href, window.location.href).pathname)) return;
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if (link.target && link.target !== '_self') return;
          if (link.hasAttribute('data-gwp-waiting')) return; // second click → browser follows the link
        } catch (e) {
          return;
        }

        let done = false;
        const go = () => {
          if (done) return;
          done = true;
          try {
            link.removeAttribute('data-gwp-waiting');
            link.removeAttribute('aria-busy');
          } catch (e) {
            /* navigate anyway */
          }
          window.location.assign(link.href);
        };

        try {
          setTimeout(go, GWP_CHECKOUT_MAX_WAIT_MS);
          event.preventDefault();
          link.setAttribute('data-gwp-waiting', '');
          link.setAttribute('aria-busy', 'true');
          this.request('checkout').then(go, go);
        } catch (e) {
          go();
        }
      },
      true
    );

    // Back from checkout via bfcache: never show a stale "busy" button.
    window.addEventListener('pageshow', () => {
      try {
        document.querySelectorAll('a[data-gwp-waiting]').forEach((a) => {
          a.removeAttribute('data-gwp-waiting');
          a.removeAttribute('aria-busy');
        });
      } catch (e) {
        /* noop */
      }
    });
  }

  /* Page load: plan from the cart rendered into the page; only fetch and fix
     when that snapshot says something is wrong. */
  processInitialCart() {
    try {
      const el = document.querySelector('[data-gwp-cart]');
      if (el) {
        const plan = this.plan(JSON.parse(el.textContent));
        if (!plan.add && !plan.updates && !plan.relabel) {
          gwpLog('page-load: nothing to do', plan);
          return;
        }
      }
    } catch (e) {
      /* fall through to a fresh check */
    }
    this.request('page-load');
  }

  /* ------------------------------------------------------------- reconcile */

  async reconcile(source) {
    let added = false;
    let removed = false;
    let changed = false;
    let reason = '';
    let spend = 0;
    let relabeled = false;

    try {
      // Re-check after each fix; three passes is plenty (add → verify).
      for (let pass = 0; pass < 3; pass++) {
        const cart = await this.fetchCart();
        const plan = this.plan(cart);
        reason = plan.reason;
        spend = plan.spend || 0;
        gwpLog(source, `pass ${pass}`, plan);

        if (!plan.add && !plan.updates && !plan.relabel) break;

        // The note is rewritten at most once per run. If it still doesn't match
        // after that, stop rather than rewriting the line on every pass.
        if (plan.relabel && !plan.add && !plan.updates && relabeled) {
          gwpLog(source, 'note still differs after one rewrite; stopping', plan);
          break;
        }

        if (plan.add) {
          await this.addGift();
          added = true;
        }
        if (plan.updates) {
          await this.updateLines(plan.updates);
          if (plan.remove) removed = true;
        }
        if (plan.relabel && !relabeled) {
          await this.relabelGift(plan.relabel);
          relabeled = true;
        }
        changed = true;
      }
    } catch (error) {
      console.error('GWP: reconcile failed', error);
      if (changed) this.refreshCartUI();
      return this.result({ success: false, changed, added, removed, reason: 'error', error: error.message, spend });
    }

    if (changed) this.refreshCartUI();
    return this.result({ changed, added, removed, reason, spend });
  }

  result({ success = true, changed = false, added = false, removed = false, reason = '', error = null, spend = 0 } = {}) {
    return {
      success,
      processed: changed,
      changed,
      giftAdded: added,
      giftRemoved: removed,
      reason,
      error,
      filteredTotal: spend,
    };
  }

  refreshCartUI() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  /* -------------------------------------------------------------- requests */

  root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Cart with collection ids + line keys (section `gwp-cart-data`). */
  async fetchCart() {
    const res = await this.fetch(`${this.root()}cart?section_id=gwp-cart-data`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`cart data HTTP ${res.status}`);

    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const el = doc.querySelector('[data-gwp-cart]');
    if (!el) throw new Error('cart data element not found');
    return JSON.parse(el.textContent);
  }

  async addGift() {
    const res = await this.fetch(`${this.root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: this.config.variantId, quantity: 1, properties: this.giftProperties() }],
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw new Error(data.description || data.message || `add HTTP ${res.status}`);
  }

  /* Replaces the gift line's properties (change.js swaps them wholesale). */
  async relabelGift(key) {
    const res = await this.fetch(`${this.root()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 1, properties: this.giftProperties() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw new Error(data.description || data.message || `change HTTP ${res.status}`);
  }

  async updateLines(updates) {
    const res = await this.fetch(`${this.root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw new Error(data.description || data.message || `update HTTP ${res.status}`);
  }

  /* ---------------------------------------------------------- event bridge */

  async handleProcessEvent(event) {
    const { source, callback } = event.detail || {};
    if (typeof callback !== 'function') return;

    try {
      callback(await this.request(source || 'event'));
    } catch (error) {
      callback(this.result({ success: false, reason: 'error', error: error.message }));
    }
  }

  /* Clears the session dismissal (testing / manual reset). */
  reset() {
    if (this.config) this.setDismissed(false);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.gwpManager = new GWPManager();
    });
  } else {
    window.gwpManager = new GWPManager();
  }
}

/**
 * Ask the GWP manager to bring the cart in line with the promo and wait for it.
 * `cartData` is ignored — the manager always reads a fresh cart.
 *
 * @param {Object} cartData - Unused, kept for backwards compatibility
 * @param {String} source - Caller label (debug only)
 * @returns {Promise<Object>} { success, changed, giftAdded, giftRemoved, reason, ... }
 */
if (typeof window !== 'undefined') window.processGWP = function (cartData, source = 'unknown') {
  return new Promise((resolve) => {
    document.dispatchEvent(
      new CustomEvent('gwp:process', {
        detail: { cartData, source, callback: resolve },
      })
    );
  });
};

// Node tests (tests/gwp-manager.test.js) load the pure logic without a browser.
if (typeof window === 'undefined' && typeof module !== 'undefined') {
  module.exports = { GWPManager, gwpPropsObject };
}
